import { Avatar, Flex, Icon, IconButton, Menu, MenuButton, MenuItem, MenuList, Text, Image } from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { getConfigFromStorage } from '@/utils/config';
import { formatDateToDateTimeDisplay } from '@/utils/formatDateToDateTimeDisplay';
import { CustomTable } from '@/components/CustomTable/CustomTable';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { Banner } from '@/model';
import { formatDateToDateDisplay } from '@/utils/formatDateToDateDisplay';
import { useTranslation } from 'react-i18next';

export interface BannerTableProps {
  data: Banner[];
  isLoading: boolean;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  totalItems: number;
  sorting?: { field: string; desc: boolean };
  onSortingChange?: (field: string, desc: boolean) => void;
  onPaginationChange: (page: number, pageSize: number) => void;
  OnEdit: (item: Banner) => void;
  OnDelete: (item: Banner) => void;
}

export const BannerTable = ({
  data,
  isLoading,
  pageNumber,
  pageSize,
  totalPages,
  sorting,
  totalItems,
  onSortingChange,
  onPaginationChange,
  OnEdit,
  OnDelete,
}: BannerTableProps) => {
  const columnHelper = createColumnHelper<Banner>();
  const isMultiTeam = getConfigFromStorage()?.is_multi_team ?? true;
  const { t } = useTranslation();

  const columns = [
    columnHelper.accessor('created_at', {
      cell: props => formatDateToDateTimeDisplay(props.getValue()),
      header: t('pages.application_banner.components.banner_table.created_at'),
    }),

    columnHelper.accessor('team_name', {
      cell: props => {
        const { team_image, team_name } = props.row.original;
        return (
          <Flex alignItems='center' gap={2}>
            {team_image ? <Image src={team_image} alt={team_name || ''} w='24px' h='24px' objectFit='contain' /> : null}
            <Text fontWeight='bold'>{team_name || ''}</Text>
          </Flex>
        );
      },
      header: t('pages.application_banner.components.banner_table.team'),
    }),

    columnHelper.accessor('title', {
      cell: props => (
        <Flex alignItems='center' gap={4}>
          <Avatar size='md' src={props.row.original.image_url} name={props.getValue()} bg='brand.50' color='white' />
          <Text>{props.getValue()}</Text>
        </Flex>
      ),
      header: t('pages.application_banner.components.banner_table.title'),
    }),
    columnHelper.accessor('partner_name', {
      cell: props => props.getValue(),
      header: t('pages.application_banner.components.banner_table.partner_name'),
    }),
    columnHelper.display({
      id: 'period',
      header: t('pages.application_banner.components.banner_table.period'),
      cell: props => (
        <>
          {props.row.original.start_at && <Text>{formatDateToDateDisplay(props.row.original.start_at)}</Text>}
          {props.row.original.end_at && <Text> a {formatDateToDateDisplay(props.row.original.end_at)}</Text>}
        </>
      ),
      enableSorting: false,
    }),

    columnHelper.display({
      id: 'actions',
      header: t('pages.application_banner.components.banner_table.actions'),
      cell: props => (
        <Flex justifyContent={'end'}>
          <Menu>
            <MenuButton
              textColor='text.table'
              as={IconButton}
              variant='ghost'
              size='sm'
              icon={<Icon as={EllipsisVerticalIcon} />}
              aria-label={t('pages.application_banner.components.banner_table.partner_aria_label')}
            />
            <MenuList textColor='white'>
              <MenuItem
                onClick={() => OnEdit?.(props.row.original as Banner)}
                icon={
                  <Text>
                    <ChakraFeatherIcon name='eye' w='18px' />
                  </Text>
                }
              >
                <Text>{t('pages.application_banner.components.banner_table.view_details')},</Text>
              </MenuItem>
              <MenuItem
                onClick={() => OnDelete?.(props.row.original as Banner)}
                icon={
                  <Text>
                    <ChakraFeatherIcon name='trash-2' color='red.500' w='18px' />
                  </Text>
                }
              >
                <Text>{t('pages.application_banner.components.banner_table.delete')}</Text>
              </MenuItem>
            </MenuList>
          </Menu>
        </Flex>
      ),
      enableSorting: false,
      meta: {
        align: 'right',
      },
    }),
  ];

  return (
    <CustomTable
      data={data}
      isLoading={isLoading}
      pageNumber={pageNumber}
      pageSize={pageSize}
      totalPages={totalPages}
      totalItems={totalItems}
      columns={isMultiTeam ? columns : columns.filter(col => (col as any).accessorKey !== 'team_name')}
      onPaginationChange={onPaginationChange}
      sorting={sorting}
      onSortingChange={onSortingChange}
    />
  );
};
